/** @format */
"use client";
import { Card, Flex, Heading, Button, Box } from "@radix-ui/themes";
import Image from "next/image";
import React, { useEffect } from "react";
import BackButton from "../backButton";
import Loader from "@/app/loader/page";
import CopyRight from "../copyRight";
import { useAuth } from "@/app/authProvider";
import { useSelector } from "react-redux";
import { redirect, useRouter } from "next/navigation";
import { RootState } from "@/app/redux/store";
import { getDataFromSessionStorage } from "@/app/services/storageService";
import { Roles } from "@/app/config/constants";
import { MixPanelService } from "@/app/services/mixpanelService";
import { InfoCircledIcon } from "@radix-ui/react-icons";

interface LoginViewProps {
  handleBack: () => void;
  enableBackAction: boolean;
  loginOptions?: string[];
  screenViewHeading: string;
  screenViewSubHeading: React.ReactNode | null;
  contentView: React.ReactNode | null;
  disableSubmitAction: boolean;
  submitActionLabel: string;
  hanldeSubmitAction: () => void;
  isLoading: boolean;
  screenViewName: string;
}

const LoginLayout: React.FC<LoginViewProps> = (props) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const myProfile = useSelector(
    (state: RootState) => state.authReducer.userProfile
  );

  const navigateToUserHome = (isForceUpdate: boolean = false) => {
    if (isForceUpdate) {
      window.location.href = "/patient"; // Force update to home page
      return;
    }

    if (
      myProfile ||
      (isAuthenticated && getDataFromSessionStorage("access-token")) ||
      isForceUpdate
    ) {
      if (
        [Roles.coach, Roles.doctor].includes(myProfile?.role || isAuthenticated)
      )
        redirect("/patient");
      else redirect("/dashboard");
    }
  };

  const onEvaluateKeyPress = (event: any) => {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode === 13) {
      event.preventDefault();
      props.hanldeSubmitAction();
    } else {
      return true;
    }
  };

  useEffect(() => {
    navigateToUserHome();
  }, [myProfile, isAuthenticated, router]);

  useEffect(() => {
    const handleStorageChange = () => {
      if (getDataFromSessionStorage("login-time")) {
        navigateToUserHome(true); // or update auth state
      }
    };

    window.addEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    MixPanelService?.track(props?.screenViewName);
  }, [props?.screenViewName]);

  if (isAuthenticated) return <Loader />;

  return (
    <main className="flex min-h-screen h-full flex-col items-center justify-center bg-white md:bg-primary-extraLight app-l-login md:py-5">
      {props?.isLoading && <Loader />}
      <form
        onKeyDown={(e) => {
          onEvaluateKeyPress(e);
        }}
        onSubmit={(e) => e.preventDefault()}
      >
        <Card
          size="3"
          className=" !w-auto md:!max-w-[450px] md:!min-w-[450px] lg:!max-w-[450px] lg:!min-w-[450px] !group !relative !rounded-lg md:!shadow-xl after:!hidden  !bg-white !z-10 !h-auto 1flex 1flex-col !mb-4"
        >
          {props?.enableBackAction && (
            <BackButton handleBack={() => props?.handleBack()} />
          )}
          <Flex justify="center" width="100%">
            <Image
              className="relative"
              src="/logoV1.webp"
              alt="logo"
              width={100}
              height={100}
            />
          </Flex>
          <Heading
            size="4"
            mt="6"
            weight="bold"
            className=" w-full text-center"
          >
            {props?.screenViewHeading}
          </Heading>

          <Flex direction="column" align="center" width="100%">
            {props?.screenViewSubHeading}
          </Flex>

          {props?.contentView && (
            <Flex
              direction="column"
              gap="5"
              className=" mt-auto md:mt-0 px-0 md:px-2"
            >
              {props?.contentView}
            </Flex>
          )}
          <Flex gap="3" mt="5" justify="end" className="px-0 md:px-2">
            <Button
              size="3"
              disabled={props?.disableSubmitAction}
              className={
                " w-full" +
                (props?.disableSubmitAction
                  ? " bg-secondary-default text-white opacity-[0.5] cursor-not-allowed"
                  : " !cursor-pointer bg-primary-default")
              }
              onClick={() => props?.hanldeSubmitAction()}
            >
              {props?.submitActionLabel}
            </Button>
          </Flex>
          {!props?.submitActionLabel.includes("Sign") &&
            !props.loginOptions?.includes("phone") && (
              <div className="-mx-6 -mb-6 mt-6 bg-gray-100 px-2 py-2 rounded-b-lg !text-center">
                <Box className="flex !items-center tracking-wide text-gray-600 text-xs gap-1 leading-normal w-full text-center justify-center">
                  <InfoCircledIcon />
                  Phone number login is temporarily disabled.
                </Box>
              </div>
            )}
        </Card>
      </form>
      <CopyRight />
    </main>
  );
};
export default LoginLayout;
